import { Injectable } from '@angular/core';
import {
  CollectionReference,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
} from '@angular/fire/firestore';
import { Category } from '../data/interfaces/Category';
import { Transaction } from '../data/interfaces/Transaction';
import { TransactionCategory } from '../data/interfaces/TransactionCategory';
import { AuthenticationFirebaseService } from './authentication-firebase.service';
import { FirebaseReferencesService } from './firebase-references.service';
import { UtilsFirebaseService } from './utils-firebase.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionFirebaseService {
  private transactionCollectionRef: CollectionReference;
  private userId: string;
  constructor(
    private firebaseReferencesService: FirebaseReferencesService,
    private authenticationService: AuthenticationFirebaseService,
    private utilsFirebaseService: UtilsFirebaseService
  ) {}

  /**
   * Get categories and assign them to the transactions
   *
   * @param transactions The array of transactions
   * @returns The array of TransactionCategory
   */
  public async computeCategoriesFromTransactionList(
    transactions: Array<Transaction>
  ): Promise<TransactionCategory[]> {
    const categories = await this.utilsFirebaseService.getDefaultCategories();
    return transactions?.map(
      (t: Transaction) =>
        ({
          transaction: t,
          category: categories.find((c: Category) => c.id === t.idCategory),
        } as TransactionCategory)
    );
  }

  /**
   * Function to get all transaction for a specific user at a specific month
   *
   * @param date The date corresponding to the month selected
   * @param isFullYear If we want the transactions of the year
   * @returns The transactions of a user for a concerned month
   */
  public async getAllTransactions(date: Date, isFullYear = false) {
    // The user id is not yet defined we need to get it
    if (!this.userId) {
      this.userId = (await this.authenticationService.getUser()).uid;
    }

    this.transactionCollectionRef =
      this.firebaseReferencesService.buildCollectionRef(this.userId);
    return new Promise<Array<Transaction>>(async (resolve) => {
      const transactionList: Array<Transaction> = [];
      const conventionnalMonthNumber = date.getMonth() + 1;
      const computedMonthValue =
        conventionnalMonthNumber < 10
          ? '0' + conventionnalMonthNumber
          : String(conventionnalMonthNumber);
      const yearAndMonth = `${date.getFullYear()}-${computedMonthValue}`;
      const q = query(
        this.transactionCollectionRef,
        where(
          'id',
          '>=',
          `${
            isFullYear ? `${date.getFullYear()}-01` : yearAndMonth
          }-01T00:00:00.000Z`
        ),
        where(
          'id',
          '<=',
          `${
            isFullYear ? `${date.getFullYear()}-12` : yearAndMonth
          }-31T23:59:59.999Z`
        ),
        orderBy('id', 'desc')
      );
      const docsSnapshots = await getDocs(q);
      docsSnapshots.forEach((docSnapshot) => {
        const transactionToAdd: Transaction = docSnapshot.data() as Transaction;
        transactionList.push(transactionToAdd);
      });

      resolve(transactionList);
    });
  }

  /**
   * Function to save a transaction to firestore
   *
   * @param transaction The transaction to save
   * @returns The transaction or the error depending on the outcome
   */
  public saveTransaction(
    transaction: Transaction,
    oldTransaction: Transaction = null
  ) {
    return new Promise<Transaction>(async (resolve, reject) => {
      // Add transaction as document
      await setDoc(
        doc(this.transactionCollectionRef, transaction.id),
        transaction
      )
        .then(async () => {
          // If there is old transaction, delete the transaction
          if (oldTransaction) {
            await this.deleteTransaction(oldTransaction);
          }
          // Resolve with the transaction
          resolve(transaction);
        })
        .catch((error) => {
          // Reject with the error
          reject(error);
        });
    });
  }

  /**
   * Function to save transactions to firestore (import case)
   *
   * @returns The transactions or the error depending on the outcome
   */
  public saveTransactions(transactions: Array<Transaction>) {
    return new Promise<Array<Transaction>>(async (resolve, reject) => {
      try {
        transactions.forEach(async (transaction) => {
          // Add transaction as document
          await setDoc(
            doc(this.transactionCollectionRef, transaction.id),
            transaction
          );
        });
        resolve(transactions);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Function to delete a transaction from firestore
   *
   * @param transaction The transaction to delete
   */
  public deleteTransaction(transaction: Transaction) {
    return new Promise<void>(async (resolve, reject) => {
      // Add transaction as document
      await deleteDoc(doc(this.transactionCollectionRef, transaction.id))
        .then(() => {
          // Resolve with the transaction
          resolve();
        })
        .catch((error) => {
          // Reject with the error
          reject(error);
        });
    });
  }
}
